"use client"

import { useState, useEffect, useRef } from "react"
import "./CurrentDischargeSummary.css"
import C from "../lib/Config"
import getHospitalDetails from "../lib/HospitalConfig"
import moment from "moment"
import React from 'react'; // Add this if not present

// Helper function to ensure a value is an array
const ensureArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    // Try to split by comma if it's a comma-separated string
    return value.split(',').map(item => item.trim()).filter(item => item);
  }
  // If it's a single object, wrap it in an array
  return [value];
};

// Add this helper function at the top of the file, after the imports
const formatDateString = (dateStr) => {
  if (!dateStr) return 'N/A';
  const parsed = moment(dateStr);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm:ss') : 'Invalid Date';
};
const calculateBSA = (height, weight) => {
  if (!height || !weight || isNaN(height) || isNaN(weight) || height <= 0 || weight <= 0) {
    return 0;
  }
  
  // Convert strings to numbers if needed
  const h = typeof height === 'string' ? parseFloat(height) : height;
  const w = typeof weight === 'string' ? parseFloat(weight) : weight;
  
  // Calculate BSA using the correct formula: sqrt((height * weight) / 3600)
  const bsa = Math.sqrt((h * w) / 3600);
  
  return bsa;
};


// Add this helper function (e.g., after calculateBSA or before the CurrentDischargeSummary component)
// Make sure React is imported at the top of your file: import React from 'react';

const formatTextWithLineBreaks = (textToFormat) => {
  if (!textToFormat) {
    return null; // Or return an empty string or React.Fragment if preferred
  }

  const parts = textToFormat.split('.');

  return parts.map((part, index, arr) => {
    const trimmedPart = part.trim();

    // If this part is an empty string resulting from a trailing period
    // (e.g., "Some text." -> split gives ["Some text", ""]),
    // and it's not the only part (i.e., the original text wasn't just "."),
    // then we don't want to render anything extra for it.
    if (trimmedPart === "" && index === arr.length - 1 && index > 0) {
      return null;
    }

    return (
      <span key={index}> {/* Using <span> allows segments to flow and <br/> to break them */}
        {trimmedPart}
        {/* Add the full stop back if this is not the last actual content part. */}
        {index < arr.length - 1 ? '.' : (textToFormat.endsWith('.') && trimmedPart !== "" ? '.' : '')}
        
        {/* Add a line break (<br />) if:
            - This is not the last part (index < arr.length - 1).
            - AND the *next* part in the array (arr[index+1]) is not undefined.
            - AND the *next* part, when trimmed, is not an empty string.
        */}
        {index < arr.length - 1 && arr[index+1] && arr[index+1].trim() !== '' && <br />}
      </span>
    );
  });
};
// Add these helper functions after the existing helper functions (around line 40-60)


// Format medication text with line breaks after each sentence
const formatMedicationText = (text) => {
  if (!text) return null;
  
  // Split by common separators and filter out empty strings
  const parts = text.split(/(\.|\+|\.\s*\+)/)
    .map(part => part.trim())
    .filter(part => part && part !== '.' && part !== '+');
  
  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {part.trim()}
      {index < parts.length - 1 && <br />}
    </React.Fragment>
  ));
};

function CurrentDischargeSummary(props) {
  //console.log('CurrentDischargeSummary component initialized with props:', props);
  
  const [showHeader, setShowHeader] = useState(true)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [chemoSittings, setChemoSittings] = useState([]);
  const [currentChemoDate, setCurrentChemoDate] = useState(null);
  const [latestDiagnosis, setLatestDiagnosis] = useState(null);
  const [testsRequested, setTestsRequested] = useState("");
  const [doctorPrescriptions, setDoctorPrescriptions] = useState([]);
  const [supportiveCare, setSupportiveCare] = useState("");
  const [vitalSigns, setVitalSigns] = useState({
    pulse: '',
    temperature: '',
    bpSystolic: '',
    bpDiastolic: '',
    spO2: '',
    bloodGlucose: '',
    glucoseTestType: '',
    followUpDate: '',
    weight: '',
    height: '',
    bmi: '',
    bsa: ''
  });
  // Add state variables for CBC and biochemistry values
  const [cbcValues, setCbcValues] = useState({});
  const [biochemistryValues, setBiochemistryValues] = useState({});
  // Get chemoSittingId from URL using 'p' parameter
  const [chemoSittingId, setChemoSittingId] = useState(() => {
    let id = null;
    if (props.location && props.location.search) {
      const params = new URLSearchParams(props.location.search);
      id = params.get('p');
    }
    //console.log('Initializing chemoSittingId from URL:', id);
    return id;
  });
  
  // Get patientId from localStorage
  const patientId = localStorage.getItem('PatientpatientId');
  
  //console.log('Initial chemoSittingId:', chemoSittingId);
  //console.log('Patient ID:', patientId);

const [hospitalDetails] = useState(getHospitalDetails);
  
  const fetchAllChemoSittings = async (patientId) => {
    try {
      const response = await fetch(`${C.getUrl()}/userCase/getAllChemoSitting/${patientId}`);
      const result = await response.json();
      
      if (result.statusCode === 200 && result.message) {
        const parsedData = JSON.parse(result.message);
        
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          // Sort by date in descending order
          const sortedData = parsedData.sort((a, b) => new Date(b.chemoDt) - new Date(a.chemoDt));
          
          // Map and format the dates
          const formattedData = sortedData.map(sitting => ({
            id: sitting.patChemoSittingId,
           // cycleNumber: sitting.chemoCycleNumber,
            date: moment(sitting.chemoDt).format('DD/MM/YYYY'),
            plannedDate: moment(sitting.chemoPlannedDt).format('DD/MM/YYYY'),
            delay: sitting.delay,
            treatmentGiven: sitting.treatmentGiven,
            nextChemoDate: sitting.nextChemoCycleDate,
            previousChemoDate: sitting.previousChemoCycleDate
          }));
          
          setChemoSittings(sortedData);
        } else {
        }
      }
    } catch (err) {
    }
  };
  
  // Initialize component data
useEffect(() => {
  if (!chemoSittingId) {
    setError('No chemo sitting ID found in URL');
    return;
  }
  
  if (!patientId) {
    setError('No patient ID found in localStorage');
    return;
  }
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // First fetch all chemo sittings to get the dates
      await fetchAllChemoSittings(patientId);
      
      // Fetch patient data and current chemo details first
      await Promise.all([
        fetchPatientData(patientId),
        fetchPatientCurrentDetails()
      ]);
      
      // Then fetch other data
      await Promise.all([
        fetchPatientMedicines(patientId),
        fetchDischargeSummary(),
        fetchPostDischargeMedications(patientId),
        fetchAdditionalMedications(patientId),
        fetchLatestDiagnosis(patientId) ,
         fetchCurrentCycleInfo(patientId)
      ]);
      
      // Note: fetchFollowupData is now called by fetchPatientCurrentDetails
      // after the chemo date is properly set
      
      setLoading(false);
    } catch (err) {
      setError('Error fetching data');
      setLoading(false);
    }
  };
  
  fetchData();
}, [chemoSittingId, patientId]);
  
  // Using individual state variables instead of a dischargeSummary object
  const [dischargeInstructions, setDischargeInstructions] = useState("");
  const [nextVisitDate, setNextVisitDate] = useState("");
  // Which source produced the value currently in `nextVisitDate`:
  //   'sitting'  — the saved next_visit_date on the chemo sitting, or a date the user
  //                just picked. Authoritative; the fallback must not overwrite it.
  //   'schedule' — derived from the next future chemo cycle. Fallback only.
  // The two loaders that set this field run concurrently (see the effects keyed on
  // chemoSittingId/patientId), so precedence is tracked here rather than relying on
  // whichever fetch happens to resolve last.
  const nextVisitDateSourceRef = useRef(null);
  const [dischargeDate, setDischargeDate] = useState('');
  const [chemoGiven, setChemoGiven] = useState(false);
  // The summary opens read-only; the user has to click "Edit Details" to change anything.
  const [isEditing, setIsEditing] = useState(false);
  const [dischargeSummaryStatus, setDischargeSummaryStatus] = useState("PENDING");
  const [followupData, setFollowupData] = useState({});
  const [allFollowupReports, setAllFollowupReports] = useState([]);
  const [adverseEffects, setAdverseEffects] = useState([]);
  const [additionalMedications, setAdditionalMedications] = useState([]);
  const [preMedications, setPreMedications] = useState([]);
  const [chemoMedications, setChemoMedications] = useState([]);
  const [postDischargeMedications, setPostDischargeMedications] = useState([]);
  const [testsPrescribed, setTestsPrescribed] = useState("");
  const [nextVisitDuration, setNextVisitDuration] = useState("");
  const [postMedications, setPostMedications] = useState({
    antiemetic: [],
    gcsf: [],
    antithrombotic: []
  });
  

  // State for patient details
  const [patientDetails, setPatientDetails] = useState({
    name: "",
    gender: "",
    age: "",
    uhid: "",
    admissionDate: "",
    admissionTime: "",
    dischargeDate: "",
    dischargeTime: "",
  })

  // Add these to your state variables section
const [medications, setMedications] = useState({
  preMedication: [],
  supplements: [],
  chemoDosage: "",
  antiemeticMedication: "",
  gcsfMedication: "",
  antithromboticMedication: "",
  additionalMedications: []
});
  // State for doctor details
  const [doctorDetails, setDoctorDetails] = useState({
    name: "",
    qualification: "",
  })


  
// Added function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
};

  // State for hospital details
  // const [hospitalDetails, setHospitalDetails] = useState({
  //   name: "Sunrise Oncology Center - Borivali",
  //   subName: "(Operated under SHREE SAISAMARTH HEALTHCARE PVT. LTD.)",
  //   address: "1st Floor, Sai Towers, Soda Wala Ln, Govind Nagar, Borivali West, Mumbai, 400092",
  //   phone1: "+912235757438",
  //   phone2: "+917400066780",
  // })

  // State for hospital details


  // State for diagnosis
  const [diagnosis, setDiagnosis] = useState({
    primarySite: "",
    allergies: "",
    comorbidities: "",
  })

  // State for MDT plan
  const [mdtPlan, setMdtPlan] = useState({
    plan: "",
    regimen: "",
    cycle: 1,
    day: 1,
    plannedCycles: 1,
    interval: 0,
    intent: "",
    height: 0,
    weight: 0,
    bsa: 0,
  })

  // State for signs on admission
  const [signsOnAdmission, setSignsOnAdmission] = useState("")

  // State for investigations notes
  const [investigationsNotes, setInvestigationsNotes] = useState("")

  // State for pre-chemo medication
  const [preChemoMedication, setPreChemoMedication] = useState([
    {
      drugName: "",
      finalDosage: true,
      notes: "",
    },
  ])

  // State for chemotherapy
  const [chemotherapy, setChemotherapy] = useState([
    {
      drugName: "",
      finalDosage: true,
      notes: "",
    },
  ])

  // State for post-chemo medication
  const [postChemoMedication, setPostChemoMedication] = useState([
    {
      drugName: "",
      finalDosage: true,
      notes: "",
    },
  ])

  // Discharge instructions state is already declared above

  // State for post discharge medication
  const [postDischargeMedication, setPostDischargeMedication] = useState([
    {
      medicine: "",
      dose: "",
      when: "",
      duration: "",
      quantity: "",
      unit: "",
    },
  ])


  // Helper function to check if Current Diagnosis has data
const hasCurrentDiagnosisData = () => {
  return (
    diagnosis.primarySite ||
    (diagnosis.allergies && diagnosis.allergies !== 'No Allergies') ||
    (diagnosis.comorbidities && diagnosis.comorbidities !== 'No Comorbidities') ||
    (latestDiagnosis && (
      latestDiagnosis.status ||
      latestDiagnosis.diagnosisType ||
      latestDiagnosis.primarySite ||
      latestDiagnosis.diagnosisDescription ||
      latestDiagnosis.description ||
      latestDiagnosis.histologyText ||
      latestDiagnosis.histology ||
      latestDiagnosis.histologyGradeText ||
      latestDiagnosis.histologyGrade ||
      (latestDiagnosis.biomarkers && latestDiagnosis.biomarkers.length > 0)
    ))
  );
};

// Helper function to check if MDT Plan has data
const hasMdtPlanData = () => {
  return (
    mdtPlan.regimen ||
    mdtPlan.regimenPhase ||
    mdtPlan.frequency ||
    mdtPlan.plannedCycles ||
    mdtPlan.firstChemoDate ||
    mdtPlan.interval ||
    mdtPlan.cycle ||
    mdtPlan.day ||
    mdtPlan.intent ||
    mdtPlan.height ||
    mdtPlan.weight ||
    mdtPlan.bsa
  );
};

// Helper function to check if Investigations Notes has data
const hasInvestigationsNotesData = () => {
  return (
    (opVisits.length > 0 && opVisits[0].vitals && opVisits[0].vitals.length > 0) ||
    (opVisits.length > 0 && opVisits[0].clinicalAssessment && opVisits[0].clinicalAssessment.onExamination) ||
    Object.keys(cbcValues).length > 0 ||
    Object.keys(biochemistryValues).length > 0 ||
    (allFollowupReports && allFollowupReports.length > 0)
  );
};

// Helper function to check if Signs On Admission has data
const hasSignsOnAdmissionData = () => {
  return (
    Object.values(vitalSigns).some(value => value !== '') ||
    (adverseEffects && adverseEffects.length > 0) ||
    (opVisits.length > 0 && opVisits[0].testsRequested) ||
    investigationsNotes
  );
};

// Helper function to check if Pre-Chemo Medication has data
const hasPreChemoMedicationData = () => {
  return (
    (medications.preMedication && medications.preMedication.length > 0) ||
    (medications.supplements && medications.supplements.length > 0)
  );
};

// Helper function to check if Chemotherapy has data
const hasChemotherapyData = () => {
  return medications.chemoDosage && medications.chemoDosage.trim() !== '';
};

// Helper function to check if Supportive Care has data
const hasSupportiveCareData = () => {
  return supportiveCare && supportiveCare.trim() !== '';
};

  // State for cycle notes
  const [cycleNotes, setCycleNotes] = useState("")

  // State for doctor notes
  const [doctorNotes, setDoctorNotes] = useState("")

  // State for OP visits and vitals
  const [opVisits, setOpVisits] = useState([]);
  const [latestVitals, setLatestVitals] = useState(null);
  
  // State for nurse notes
  const [nurseNotes, setNurseNotes] = useState("");

  // CBC and Biochemistry values are already declared at the top of the component

useEffect(() => {
    if (!patientId) {
      setError('Patient ID not found');
      setLoading(false);
      return;
    }

    if (!chemoSittingId) {
      setError('Chemo Sitting ID not found');
      setLoading(false);
      return;
    }



    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all required data
        await Promise.all([
          fetchPatientData(patientId),
          fetchFollowupData(patientId),
          fetchPatientMedicines(patientId),
          fetchPostDischargeMedications(patientId),
          fetchAdditionalMedications(patientId),
          fetchDischargeSummary(),
          fetchDoctorPrescriptionPatientMedicines(patientId),
          fetchPatientCurrentDetails(),
          fetchPatientOpVisits(patientId),
          fetchChemoTemplateData()
        ]);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [patientId, chemoSittingId]);
  
  const fetchLatestDiagnosis = async (patientId) => {
    try {
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/get/${patientId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
      });
  
      if (!response.ok) {
        throw new Error(`Failed to fetch diagnoses. Status: ${response.status}`);
      }
  
      const data = await response.json();
      
      // Process the data based on format
      let processedData = [];
      if (data && data.statusCode === 200 && data.message) {
        // Handle API response with statusCode/message structure
        try {
          processedData = JSON.parse(data.message);
        } catch (e) {
          processedData = data.message;
        }
      } else if (Array.isArray(data)) {
        // Direct array of diagnoses
        processedData = data;
      } else if (data && data.diagnoses && Array.isArray(data.diagnoses)) {
        // Nested diagnoses array
        processedData = data.diagnoses;
      } else if (data && typeof data === 'object') {
        // Single diagnosis object
        processedData = [data];
      }
  
      // Ensure it's an array
      if (!Array.isArray(processedData)) {
        processedData = [];
      }
  
      // Sort diagnoses by date (newest first)
      const sortedDiagnoses = [...processedData].sort((a, b) => {
        // Parse dates for comparison
        const dateA = a.dateOfDiagnosis ? new Date(a.dateOfDiagnosis) : new Date(0);
        const dateB = b.dateOfDiagnosis ? new Date(b.dateOfDiagnosis) : new Date(0);
        // Sort in descending order (newest first)
        return dateB - dateA;
      });
  
      // Set the latest diagnosis if available, with additional processing
      if (sortedDiagnoses.length > 0) {
        const latestDiag = sortedDiagnoses[0];
        
        // Pre-process and add flattened fields for easier access
        if (latestDiag.histologyList && latestDiag.histologyList.length > 0) {
          latestDiag.histologyText = latestDiag.histologyList[0].histologyDescription;
        }
        
        if (latestDiag.histologyGrades && latestDiag.histologyGrades.length > 0) {
          latestDiag.histologyGradeText = latestDiag.histologyGrades[0].gradeDescription;
        }
        
        setLatestDiagnosis(latestDiag);
      } else {
        setLatestDiagnosis(null);
      }
    } catch (err) {
      setLatestDiagnosis(null);
    }
  };

  const fetchPatientOpVisits = async (patientId) => {
    try {
      const response = await fetch(`${C.getUrl()}/patientOpVisit/get/${patientId}`);

      // Check if the response is successful
      if (!response.ok) {
        const errorText = await response.text();
        return; // Exit silently as this is a secondary data fetch
      }

      const result = await response.json();
      setOpVisits(result);
    } catch (error) {
    }
  };

  const fetchChemoTemplateData = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/userCase/getChemoTemplateForNurse/${chemoSittingId}`);
      const result = await response.json();
      if (result.statusCode === 200 && result.message && result.message !== 'No record found.') {
        const tempData = JSON.parse(result.message);
        if (tempData.nextVisitInstructions) {
          setTestsRequested(tempData.nextVisitInstructions);
        }
      }
    } catch (error) {
    }
  };

  // Helper function to extract nested diagnosis data
const extractDiagnosisDetails = (diagnosis) => {
  if (!diagnosis) return { histology: 'N/A', histologyGrade: 'N/A' };

  // Extract histology description
  let histology = 'N/A';
  if (diagnosis.histologyList && diagnosis.histologyList.length > 0) {
    histology = diagnosis.histologyList[0].histologyDescription;
  } else if (diagnosis.histology) {
    histology = diagnosis.histology;
  }

  // Extract histology grade description
  let histologyGrade = 'N/A';
  if (diagnosis.histologyGrades && diagnosis.histologyGrades.length > 0) {
    histologyGrade = diagnosis.histologyGrades[0].gradeDescription;
  } else if (diagnosis.histologyGrade) {
    histologyGrade = diagnosis.histologyGrade;
  }

  return { histology, histologyGrade };
};

  // Function to fetch additional medications using the correct API endpoint
  const fetchAdditionalMedications = async (patientId) => {
    try {
      const response = await fetch(
        `${C.getUrl()}/additionalMedicationListXref/getPatientAdditionalMedications?patientId=${patientId}`
      );
      const result = await response.json();
      
      if (
        result.statusCode === 200 &&
        result.message &&
        result.message !== "No records found"
      ) {
        const medicines = JSON.parse(result.message);
        const activeMedicines = medicines.filter((m) => m.activeInd === "Y");
        
        if (activeMedicines.length > 0) {
          const medicationDetails = activeMedicines.map((m) => ({
            medicine: m.medicineName,
            dose: m.dose,
            when: m.when,
            duration: m.duration,
            instructions: m.instructions
          }));
          
          setAdditionalMedications(medicationDetails);
        } else {
          setAdditionalMedications([]);
        }
      } else {
        setAdditionalMedications([]);
      }
    } catch (err) {
      setAdditionalMedications([]);
    }
  };


  const fetchFollowupData = async (patientId, directChemoDate = null) => {
    try {
      setLoading(true);
  
      // Get and parse current chemo date first
      let chemoDate = directChemoDate;
      if (!chemoDate && currentChemoDate) {
        // Try multiple date formats for parsing
        const parsedChemoDate = moment(currentChemoDate, [
          'DD/MM/YYYY',
          'MMM DD, YYYY, hh:mm:ss A',
          'YYYY-MM-DD HH:mm:ss',
          'YYYY-MM-DD'
        ]);
      
        if (parsedChemoDate.isValid()) {
          chemoDate = parsedChemoDate.format('DD/MM/YYYY');
        } else {
        }
      } else if (directChemoDate) {
      } else {
      }
  
      // First fetch all follow-up appointments
      const followupResponse = await fetch(`${C.getUrl()}/followupAppt/findAll/${patientId}`);
      if (!followupResponse.ok) {
        throw new Error(`HTTP error! status: ${followupResponse.status}`);
      }
      const followupResult = await followupResponse.json();
  
  
      // Parse and standardize the followup appointments
      let followupAppointments = [];
      let currentAppointment = null;
  
      if (followupResult.statusCode === 200 && followupResult.message) {
        let followupDetails;
        try {
          followupDetails = typeof followupResult.message === 'string' ? 
            JSON.parse(followupResult.message) : followupResult.message;
  
          // Check for a single appointment structure (first-time patients might only have one appointment)
          if (followupDetails.appointmentId) {
            // This is a single appointment, not a list
            const parsedDate = moment(followupDetails.appointmentDt, [
              'MMM DD, YYYY, hh:mm:ss A',
              'YYYY-MM-DD HH:mm:ss',
              'YYYY-MM-DD',
              'DD/MM/YYYY'
            ]);
  
            if (parsedDate.isValid()) {
              currentAppointment = {
                ...followupDetails,
                standardDate: parsedDate.format('DD/MM/YYYY'),
                momentDate: parsedDate
              };
              followupAppointments.push(currentAppointment);
            }
          }
  
          // Process all appointments if available
          if (followupDetails.followUpAppointments) {
            const appointments = followupDetails.followUpAppointments
              .filter(apt => apt.activeInd === 'Y') // Only active appointments
              .map(appointment => {
                // Try multiple date formats for parsing
                const parsedDate = moment(appointment.appointmentDt, [
                  'MMM DD, YYYY, hh:mm:ss A',
                  'YYYY-MM-DD HH:mm:ss',
                  'YYYY-MM-DD',
                  'DD/MM/YYYY'
                ]);
  
  
                return {
                  ...appointment,
                  standardDate: parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : null,
                  momentDate: parsedDate // Keep moment object for comparison
                };
              })
              .filter(apt => apt.standardDate !== null);
  
            // Add appointments to the list if they're not already there
            appointments.forEach(apt => {
              if (!followupAppointments.some(existingApt => existingApt.appointmentId === apt.appointmentId)) {
                followupAppointments.push(apt);
              }
            });
          }
  
          // Sort by date in descending order
          followupAppointments = followupAppointments.sort((a, b) =>
            b.momentDate.valueOf() - a.momentDate.valueOf()
          );
  
        } catch (e) {
          setAllFollowupReports([]);
          resetLabValues();
          setLoading(false);
          return;
        }
      } else {
        setAllFollowupReports([]);
        resetLabValues();
        setLoading(false);
        return;
      }
  
      // Find matching appointment based on chemo date
      let matchingAppointment = null;
  
      // FIRST TIME PATIENT FIX: If there's only one appointment and one chemo sitting
      // (which is common for first-time patients), use that appointment
      if (followupAppointments.length === 1 && chemoDate) {
        matchingAppointment = followupAppointments[0];
      } else if (chemoDate && followupAppointments.length > 0) {
        // First try exact date match
        matchingAppointment = followupAppointments.find(
          apt => apt.standardDate === chemoDate
        );
  
        if (matchingAppointment) {
        } else {
  
          // If no exact match, find the most recent previous appointment
          const chemoMoment = moment(chemoDate, 'DD/MM/YYYY');
  
          // Filter appointments before or on chemo date
          const previousAppointments = followupAppointments
            .filter(apt => apt.momentDate.isSameOrBefore(chemoMoment))
            .sort((a, b) => b.momentDate.valueOf() - a.momentDate.valueOf());
  
          if (previousAppointments.length > 0) {
            matchingAppointment = previousAppointments[0];
          } else {
            // If no previous appointments, use the most recent one
            matchingAppointment = followupAppointments[0];
          }
        }
      } else if (followupAppointments.length > 0) {
        // If no chemo date but we have appointments, use the most recent
        matchingAppointment = followupAppointments[0];
      } else {
        setAllFollowupReports([]);
        resetLabValues();
        setLoading(false);
        return;
      }
  
      if (matchingAppointment) {
  
        // Extract and set adverse effects
        if (matchingAppointment.followupAdverseEffectDTOList && matchingAppointment.followupAdverseEffectDTOList.length > 0) {
          // Map the DTO list to a simpler format for display if needed,
          // or just store the DTOs as is, depending on how you want to display.
          // For now, let's store the list as is.
          setAdverseEffects(matchingAppointment.followupAdverseEffectDTOList);
        } else {
          setAdverseEffects([]); // Ensure state is empty if no adverse effects
        }
  
        // Reset lab values before setting new ones
        resetLabValues();
  
        // Save matching appointment to state
        setAllFollowupReports([matchingAppointment]);
        await fetchVitalSigns(matchingAppointment.appointmentId);

        // The findById endpoint returns a thinner DTO — supplement with height,
        // glucoseTestType from the findAll response, then derive BMI and BSA.
        if (matchingAppointment.height || matchingAppointment.glucoseTestType) {
          const height = matchingAppointment.height || 0;
          const weight = matchingAppointment.weight || 0;
          const bmi = height && weight
            ? (weight / Math.pow(height / 100, 2)).toFixed(2)
            : '';
          const bsa = height && weight
            ? Math.sqrt((height * weight) / 3600).toFixed(2)
            : '';
          setVitalSigns(prev => ({
            ...prev,
            height: height ? String(height) : prev.height,
            glucoseTestType: matchingAppointment.glucoseTestType || prev.glucoseTestType,
            bmi: bmi || prev.bmi,
            bsa: bsa || prev.bsa
          }));
        }
  
        // Fetch and process lab reports
        if (matchingAppointment.appointmentReports && matchingAppointment.appointmentReports.length > 0) {
  
          for (const report of matchingAppointment.appointmentReports) {
            try {
  
              const reportResponse = await fetch(
                `${C.getUrl()}/followupAppt/appointmentReport/${report.apptXReportId}`
              );
  
              if (!reportResponse.ok) {
                throw new Error(`HTTP error fetching report! status: ${reportResponse.status}`);
              }
  
              const reportResult = await reportResponse.json();
  
              if (reportResult.statusCode === 200 && reportResult.message) {
                let reportData;
                try {
                  reportData = typeof reportResult.message === 'string' ?
                    JSON.parse(reportResult.message) : reportResult.message;
  
  
                  if (report.reportMasterDTO.reportName === 'CBC') {
                    // Create a new object to store all CBC parameters dynamically
                    const newCbcData = {};
  
                    if (reportData && reportData.reportValues) {
                      reportData.reportValues.forEach(val => {
                        if (val && val.reportParameterMstDTO && val.value) {
                          const paramName = val.reportParameterMstDTO.reportParamName;
  
                          // Dynamically add all parameters with values
                          newCbcData[paramName] = val.value;
                        }
                      });
                    }
  
                    setCbcValues(newCbcData);
                  }
                  else if (report.reportMasterDTO.reportName === 'Biochemistry') {
                    // Create a new object to store all Biochemistry parameters dynamically
                    const newBiochemData = {};
  
                    if (reportData && reportData.reportValues) {
                      reportData.reportValues.forEach(val => {
                        if (val && val.reportParameterMstDTO && val.value) {
                          const paramName = val.reportParameterMstDTO.reportParamName;
  
                          // Dynamically add all parameters with values
                          newBiochemData[paramName] = val.value;
                        }
                      });
                    }
  
                    setBiochemistryValues(newBiochemData);
                  }
                } catch (parseError) {
                }
              } else {
              }
            } catch (reportError) {
            }
          }
        } else if (matchingAppointment.appointmentId) {
          // If we have an appointment ID but no reports directly in the object,
          // try to fetch reports separately (sometimes needed for first-time appointments)
  
          try {
            const appointmentResponse = await fetch(
              `${C.getUrl()}/followupAppt/${matchingAppointment.appointmentId}`
            );
  
            if (appointmentResponse.ok) {
              const appointmentResult = await appointmentResponse.json();
  
              if (appointmentResult.statusCode === 200 && appointmentResult.message) {
                let appointmentData;
                try {
                  appointmentData = typeof appointmentResult.message === 'string' ? 
                    JSON.parse(appointmentResult.message) : appointmentResult.message;
  
  
                  // Also try to get adverse effects from the detailed appointment data
                  if (appointmentData.followupAdverseEffectDTOList && appointmentData.followupAdverseEffectDTOList.length > 0) {
                     setAdverseEffects(appointmentData.followupAdverseEffectDTOList);
                  } else {
                     // Don't clear if already set from initial fetch, unless this is the only source
                     // For now, we'll let the initial fetch handle this.
                  }
  
  
                  if (appointmentData.appointmentReports && appointmentData.appointmentReports.length > 0) {
  
                    // Now we have reports, process them
                    for (const report of appointmentData.appointmentReports) {
                      if (report.reportMasterDTO.reportName === 'CBC') {
                        // Process CBC report
                        await fetchReportData(report.apptXReportId, 'CBC');
                      }
                      else if (report.reportMasterDTO.reportName === 'Biochemistry') {
                        // Process Biochemistry report
                        await fetchReportData(report.apptXReportId, 'Biochemistry');
                      }
                    }
                  }
                } catch (parseError) {
                }
              }
            }
          } catch (appointmentError) {
          }
        } else {
          resetLabValues();
        }
      } else {
        setAllFollowupReports([]);
        resetLabValues();
        setAdverseEffects([]); // Also clear adverse effects
      }
  
    } catch (error) {
      setAllFollowupReports([]);
      resetLabValues();
      setAdverseEffects([]); // Also clear adverse effects on error
    } finally {
      setLoading(false);
    }
  };
  
  const fetchReportData = async (reportId, reportType) => {
    try {
      
      const reportResponse = await fetch(
        `${C.getUrl()}/followupAppt/appointmentReport/${reportId}`
      );
      
      if (!reportResponse.ok) {
        throw new Error(`HTTP error fetching report! status: ${reportResponse.status}`);
      }
      
      const reportResult = await reportResponse.json();
      
      if (reportResult.statusCode === 200 && reportResult.message) {
        let reportData;
        try {
          reportData = typeof reportResult.message === 'string' ? 
            JSON.parse(reportResult.message) : reportResult.message;
          
          
          if (reportType === 'CBC') {
            // Create a new object to store all CBC parameters dynamically
            const newCbcData = {};
            
            if (reportData && reportData.reportValues) {
              reportData.reportValues.forEach(val => {
                if (val && val.reportParameterMstDTO && val.value) {
                  const paramName = val.reportParameterMstDTO.reportParamName;
                  
                  // Dynamically add all parameters with values
                  newCbcData[paramName] = val.value;
                }
              });
            }
            
            setCbcValues(newCbcData);
          } 
          else if (reportType === 'Biochemistry') {
            // Create a new object to store all Biochemistry parameters dynamically
            const newBiochemData = {};
            
            if (reportData && reportData.reportValues) {
              reportData.reportValues.forEach(val => {
                if (val && val.reportParameterMstDTO && val.value) {
                  const paramName = val.reportParameterMstDTO.reportParamName;
                  
                  // Dynamically add all parameters with values
                  newBiochemData[paramName] = val.value;
                }
              });
            }
            
            setBiochemistryValues(newBiochemData);
          }
        } catch (parseError) {
        }
      } else {
      }
    } catch (reportError) {
    }
  };
  

  const resetLabValues = () => {
    // Reset to empty objects to handle dynamic values
    setCbcValues({});
    setBiochemistryValues({});
  };
// Add this function after fetchFollowupData or other similar fetch functions
const fetchVitalSigns = async (appointmentId) => {
  try {
    if (!appointmentId) {
      return;
    }

    // Use the findById endpoint from the provided API
    const response = await fetch(`${C.getUrl()}/followupAppt/${appointmentId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.statusCode === 200 && result.message) {
      let appointmentData;
      try {
        appointmentData = typeof result.message === 'string' ? 
          JSON.parse(result.message) : result.message;
        
        
        // Update the vital signs state with the fetched data
        const newVitalSigns = {
          pulse: appointmentData.pulse || '',
          temperature: appointmentData.temperature || '',
          bpSystolic: appointmentData.bpSystolic || '',
          bpDiastolic: appointmentData.bpDiastolic || '',
          spO2: appointmentData.spO2 || '',
          bloodGlucose: appointmentData.bloodGlucose || '',
          glucoseTestType: appointmentData.glucoseTestType || '',
          weight: appointmentData.weight || '',
          height: appointmentData.height || '',
          bmi: appointmentData.bmi || '',
          bsa: appointmentData.bsa || ''
        };

        setVitalSigns(newVitalSigns);
        
        // Also update lab values if reports exist
        if (appointmentData.appointmentReports && appointmentData.appointmentReports.length > 0) {
          
          for (const report of appointmentData.appointmentReports) {
            if (report.reportMasterDTO.reportName === 'CBC') {
              await fetchReportData(report.apptXReportId, 'CBC');
            } 
            else if (report.reportMasterDTO.reportName === 'Biochemistry') {
              await fetchReportData(report.apptXReportId, 'Biochemistry');
            }
          }
        }
        
      } catch (parseError) {
      }
    } else {
    }
  } catch (error) {
  }
};

  // Add state change detection
useEffect(() => {
}, [cbcValues]);

useEffect(() => {
}, [biochemistryValues]);

  useEffect(() => {
    // Extract parameters from URL
    const searchParams = new URLSearchParams(window.location.search);
    const urlChemoSittingId = searchParams.get('chemoSittingId');
    const patientId = searchParams.get('p') || localStorage.getItem('PatientpatientId');
    
    //console.log('URL params:', { urlChemoSittingId, patientId });
    
    // IMPORTANT: In this application, when no specific chemoSittingId is provided,
    // we use the patientId as the chemoSittingId as well
    if (urlChemoSittingId && !chemoSittingId) {
      //console.log('Setting chemoSittingId from URL:', urlChemoSittingId);
      setChemoSittingId(urlChemoSittingId);
    } else if (chemoSittingId) {
      //console.log('Using existing chemoSittingId:', chemoSittingId);
      fetchDischargeSummary();
      fetchPatientCurrentDetails();
      
      // Directly call fetchPatientMedicines with the patient ID
      if (patientId) {
        //console.log('Directly calling fetchPatientMedicines with patientId:', patientId);
        fetchPatientMedicines(patientId);
      } else {
        //console.error('Cannot fetch patient medicines: No patient ID available');
      }
    } else if (patientId) {
      // If we have a patientId but no chemoSittingId, use the patientId as the chemoSittingId
     // console.log('Using patientId as chemoSittingId:', patientId);
      setChemoSittingId(patientId);
      
      // Also directly call fetchPatientMedicines with the patient ID
      //console.log('Directly calling fetchPatientMedicines with patientId:', patientId);
      fetchPatientMedicines(patientId);
    }
  }, [chemoSittingId, window.location.search, props.patientId]);
  
  // Second useEffect to handle the case when chemoSittingId is updated from URL
  // useEffect(() => {
  //   if (chemoSittingId) {
  //     fetchDischargeSummary();
  //     fetchPatientCurrentDetails();
  //   }
  // }, [chemoSittingId]);
  // Add this useEffect after the existing ones (around line 650)


  const fetchDoctorPrescriptionPatientMedicines = async (patientId) => {
    try {
      
      const response = await fetch(`${C.getUrl()}/medicineListXref/getPatientMedicines?patientId=${patientId}`);
      const data = await response.json();
      
      
      if (data.statusCode === 200 && data.message) {
        try {
          const medicines = JSON.parse(data.message);
          
          if (Array.isArray(medicines) && medicines.length > 0) {
            // Format medicines for doctor prescriptions
            const formattedMedicines = medicines.map(med => ({
              medicine: med.medicineName,
              dose: med.dose,
              when: med.when,
              duration: med.duration,
              quantity: med.quantity || "",
              unit: med.unit || "",
              instructions: med.instructions,
              medicineListXrefId: med.medicineListXrefId
            }));
            
            // Set doctor prescriptions
            setDoctorPrescriptions(formattedMedicines);
  
            // Existing OP visits update logic remains the same
            setOpVisits(prevVisits => {
              return prevVisits.map(visit => {
                if (!visit.prescription || visit.prescription.length === 0) {
                  return {
                    ...visit,
                    prescription: formattedMedicines
                  };
                }
                return visit;
              });
            });
          }
        } catch (parseError) {
        }
      }
    } catch (error) {
    }
  };
  // Fetch medications based on chemo sitting ID
  const fetchPatientMedicines = async (patientId) => {
    try {
      //console.log('Fetching medications for chemo sitting ID:', chemoSittingId, 'and patientId:', patientId);
      const response = await fetch(`${C.getUrl()}/userCase/getChemoSittingDetails?patientId=${patientId}&chemoSittingId=${chemoSittingId}`);
      //console.log('API URL:', response.url);
      const data = await response.json();
      //console.log('Raw API response:', data);
      
      if (data.statusCode === 200) {
        let chemoData;
        try {
          // First try to parse the data from the response
          if (data.data) {
            chemoData = data.data;
            //console.log('Using data field from response');
          } else if (typeof data.message === 'string') {
            try {
              chemoData = JSON.parse(data.message);
             // console.log('Parsed message field from response');
            } catch (parseError) {
              //console.log('Message field is not JSON, using as-is');
              chemoData = data.message;
            }
          } else if (data.message) {
            //console.log('Using message field (object) from response');
            chemoData = data.message;
          }
        } catch (e) {
          chemoData = data.message || {};
        }
        
        
        // Check if the data is nested inside patientChemoSittingDTO
        if (chemoData && chemoData.patientChemoSittingDTO) {
          //console.log('Found nested patientChemoSittingDTO, using it for medication data');
          chemoData = chemoData.patientChemoSittingDTO;
        }
        
        
        // Extract medication data from chemo sitting details with improved field handling
        const medicationData = {
          preMedication: [],
          supplements: [],
          chemoDosage: "",
          antiemeticMedication: "",
          gcsfMedication: "",
          antithromboticMedication: "",
          antihistamineMedication: "",
          steroidMedication: "",
          additionalMedications: []
        };
        
        if (chemoData.supportive_care) {
          setSupportiveCare(chemoData.supportive_care);
        } else {
          setSupportiveCare("");
        }

        // Set premedications if available - check multiple possible field names
        const preMeds = ensureArray(chemoData.premedications || chemoData.preMedication || chemoData.preMeds || []);
        //console.log('Found pre-medications:', preMeds);
        
        preMeds.forEach(med => {
          if (typeof med === 'object' && med !== null) {
            // If it's already an object with the right structure
            medicationData.preMedication.push({
              name: med.name || med.drugName || med.medicine || '',
              dose: med.dose || med.dosage || '',
              notes: med.notes || med.instructions || ''
            });
          } else if (med && typeof med === 'string') {
            // If it's a string
            medicationData.preMedication.push({
              name: med.trim(),
              dose: "",
              notes: ""
            });
          }
        });
        
        // Set supplements if available - check multiple possible field names
        const supplements = ensureArray(chemoData.supplements || chemoData.supplement || []);
        //console.log('Found supplements:', supplements);
        
        supplements.forEach(supp => {
          if (typeof supp === 'object' && supp !== null) {
            // If it's already an object with the right structure
            medicationData.supplements.push({
              name: supp.name || supp.drugName || supp.medicine || '',
              dose: supp.dose || supp.dosage || '',
              notes: supp.notes || supp.instructions || ''
            });
          } else if (supp && typeof supp === 'string') {
            // If it's a string
            medicationData.supplements.push({
              name: supp.trim(),
              dose: "",
              notes: ""
            });
          }
        });
        
        // Set chemotherapy details - check multiple possible field names
        medicationData.chemoDosage = chemoData.treatmentGiven || chemoData.chemoDosage || chemoData.chemoMedication || chemoData.chemotherapy || "";
        //console.log('Found chemo dosage:', medicationData.chemoDosage);
        
        // Set antiemetic medication - check multiple possible field names
        medicationData.antiemeticMedication = chemoData.antiemeticDrugs || chemoData.antiemeticMedication || chemoData.antiemetic || "";
        //console.log('Found antiemetic medication:', medicationData.antiemeticMedication);
        
        // Set GCSF medication - check multiple possible field names
        medicationData.gcsfMedication = chemoData.gcsfMedicine || chemoData.gcsfMedication || chemoData.gcsf || "";
        //console.log('Found GCSF medication:', medicationData.gcsfMedication);
        
        // Set antithrombotic medication - check multiple possible field names
        medicationData.antithromboticMedication = chemoData.antithromboticDrugs || chemoData.antithromboticMedication || chemoData.antithrombotic || "";
        //console.log('Found antithrombotic medication:', medicationData.antithromboticMedication);
        
        // Set antihistamine medication - check multiple possible field names
        medicationData.antihistamineMedication = chemoData.antihistamineDrugs || chemoData.antihistamineMedication || chemoData.antihistamine || "";
        //console.log('Found antihistamine medication:', medicationData.antihistamineMedication);
        
        // Set steroid medication - check multiple possible field names
        medicationData.steroidMedication = chemoData.steroidDrugs || chemoData.steroidMedication || chemoData.steroid || "";
        //console.log('Found steroid medication:', medicationData.steroidMedication);
      
        // Set additional medications if available - ONLY from postChemoDrugs to ensure separation from other medication types
        const additionalMeds = ensureArray(chemoData.postChemoDrugs || []);
        
        // Clear any existing additional medications to ensure we only use post-chemo drugs
        medicationData.additionalMedications = [];
        
        additionalMeds.forEach(med => {
          if (typeof med === 'object' && med !== null) {
            // If it's already an object with the right structure
            medicationData.additionalMedications.push({
              medicine: med.medicine || med.name || med.drugName || '',
              dose: med.dose || med.dosage || '',
              when: med.when || med.frequency || '',
              duration: med.duration || '',
              instructions: med.instructions || med.notes || ''
            });
          } else if (med && typeof med === 'string') {
            // If it's a string
            medicationData.additionalMedications.push({
              medicine: med.trim(),
              dose: "",
              when: "",
              duration: "",
              instructions: ""
            });
          }
        });

        
        // Set medications in state
        setMedications(medicationData);
        
        // Update UI components with the medication data
        if (medicationData.preMedication.length > 0) {
         // console.log('Setting pre-medications with:', medicationData.preMedication);
          const formattedPreMeds = medicationData.preMedication.map(med => ({
            drugName: med.name,
            finalDosage: true,
            notes: med.notes || ""
          }));
          //console.log('Formatted pre-medications:', formattedPreMeds);
          setPreMedications(formattedPreMeds);
        } else {
          //console.log('No pre-medications found, using default');
          setPreMedications([{
            drugName: "INJ GRANISET 1 MG + INJ PAN 40 MG + INJ DEXA 12 MG",
            finalDosage: true,
            notes: "IN 100 CC NS IV OVER 10 MINUTES"
          }]);
        }
        
        if (medicationData.chemoDosage) {
          //console.log('Setting chemo medications with:', medicationData.chemoDosage);
          setChemoMedications([{
            drugName: medicationData.chemoDosage,
            finalDosage: true,
            notes: "IN 250 CC NS IV OVER 60 MINUTES"
          }]);
        } else {
          const regimenName = mdtPlan?.regimen || "CHEMOTHERAPY";
          setChemoMedications([{
            drugName: `INJ ${regimenName} DOSE`,
            finalDosage: true,
            notes: "IN 250 CC NS IV OVER 60 MINUTES"
          }]);
        }
        
        // Set post-chemo medications
        
        const postMedsData = {
          antiemetic: medicationData.antiemeticMedication ? [{
            name: medicationData.antiemeticMedication,
            dose: "",
            duration: ""
          }] : [],
          gcsf: medicationData.gcsfMedication ? [{
            name: medicationData.gcsfMedication,
            dose: "",
            duration: ""
          }] : [],
          antithrombotic: medicationData.antithromboticMedication ? [{
            name: medicationData.antithromboticMedication,
            dose: "",
            duration: ""
          }] : []
        };
        
        //console.log('Formatted post medications:', postMedsData);
        setPostMedications(postMedsData);
        
        // Set additional medications
       // console.log('Additional medications:', medicationData.additionalMedications);
        if (medicationData.additionalMedications.length > 0) {
        //  console.log('Setting additional medications state');
          setAdditionalMedications(medicationData.additionalMedications);
        } else {
         // console.log('No additional medications to set');
        }
        
        // Set nurse notes if available
        if (chemoData.nurseInstructions) {
          setNurseNotes(chemoData.nurseInstructions);
        }
        
        return; // Exit early if we successfully processed chemo sitting data
      }
    } catch (err) {
      // Set default values in case of error
      setMedications({
        preMedication: [],
        supplements: [],
        chemoDosage: "",
        antiemeticMedication: "",
        gcsfMedication: "",
        antithromboticMedication: "",
        additionalMedications: []
      });
      
      // Set default medication values
      setDefaultMedicationValues();
    }
  };

  // Add this helper function to format dates in DD-MM-YYYY format
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};
  
  // Helper function to set default values
  const setDefaultMedicationValues = () => {
    setPreMedications([]);
    setChemoMedications([]);
  };

  const setDefaultAdditionalMedications = () => {
    setAdditionalMedications([]);
  };

  const fetchCurrentCycleInfo = async (patientId) => {
  try {
    const response = await fetch(`${C.getUrl()}/userCase/getTreatmentDataForNewChemo?patientId=${patientId}`);
    if (!response.ok) {
      return; // Don't proceed if the fetch failed
    }
    const result = await response.json();

    if (result.statusCode === 200 && result.message) {
      const apiDataArray = JSON.parse(result.message);
      if (apiDataArray && apiDataArray.length > 0) {
        const treatmentInfo = apiDataArray[0];
        const chemoSchedules = treatmentInfo.chemoSchedules || [];

        // Use same logic as ChemoTemplate: new Date() comparison, pick most recent past schedule
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let currentSchedule = null;
        chemoSchedules.forEach(schedule => {
          const scheduleDate = new Date(schedule.chemoDt);
          scheduleDate.setHours(0, 0, 0, 0);
          if (scheduleDate <= today) {
            if (!currentSchedule || scheduleDate > new Date(currentSchedule.chemoDt).setHours(0,0,0,0)) {
              currentSchedule = schedule;
            }
          }
        });

        const determinedCycleNumber = currentSchedule ? currentSchedule.chemoCycleNumber : null;
        const determinedTreatmentDay = currentSchedule ? currentSchedule.treatmentDayNumber : null;

        if (determinedCycleNumber !== null || determinedTreatmentDay !== null) {
          setMdtPlan(prevMdtPlan => ({
            ...prevMdtPlan,
            cycle: determinedCycleNumber !== null ? determinedCycleNumber : prevMdtPlan.cycle,
            day: determinedTreatmentDay !== null ? determinedTreatmentDay : prevMdtPlan.day
          }));
        }

      }
    }
  } catch (err) {
  }
};

  const fetchPatientData = async (id) => {
    try {
      // Validate patient ID before making API call
      if (!id || id === 'undefined' || id === undefined) {
        return;
      }
      
      setLoading(true);
      const patientResponse = await fetch(`${C.getUrl()}/userCase/getPatientSummaryDetails?patientId=${id}`);
      const patientResult = await patientResponse.json();
      
      if (patientResult.statusCode === 200) {
        const patientDetails = JSON.parse(patientResult.message);
        setPatientData(patientDetails);
        
        // Populate the form data from API response
        populateFormData(patientDetails);
  
        // Fetch appointment reports - but don't set lab values here
        const followupResponse = await fetch(`${C.getUrl()}/followupAppt/findAll/${id}`);
        const followupResult = await followupResponse.json();
        
        if (followupResult.statusCode === 200) {
          // We still fetch the data for other purposes, but we don't process lab reports here
          const followupData = JSON.parse(followupResult.message);
          
          // We can use this data for other purposes if needed, but we don't set CBC or Biochemistry values
          // This prevents conflicts with the fetchFollowupData function
          
          // Lab values will now be exclusively set by fetchFollowupData
        }
      } else {
        setError('Failed to fetch patient data');
      }
    } catch (err) {
      setError('An error occurred while fetching patient data');
    } finally {
      setLoading(false);
    }
  };

  
  const populateFormData = (data) => {
    if (!data) return;

    // Set patient details
    const patDetails = data.patientDetailsDto || {};
    const patDOB = new Date(patDetails.dob);
    const today = new Date();
    let age = today.getFullYear() - patDOB.getFullYear();
    
    // Calculate age more precisely
    if (
      today.getMonth() < patDOB.getMonth() ||
      (today.getMonth() === patDOB.getMonth() && today.getDate() < patDOB.getDate())
    ) {
      age--;
    }

    setPatientDetails(prev => ({
      ...prev,
      name: `${patDetails.firstName || ''} ${patDetails.middleName || ''} ${patDetails.lastName || ''}`.trim(),
      gender: patDetails.genderMstDto?.genderName || 'Other',
      age: age.toString(),
      uhid: patDetails.caseId || '',
      dischargeDate: moment().format('YYYY-MM-DD'),
      dischargeTime: moment().format('HH:mm:ss'),
      // admissionDate and admissionTime are set from chemoDt in fetchPatientCurrentDetails
    }));

    // Set doctor details
    if (patDetails.doctor) {
      setDoctorDetails({
        name: `Dr. ${patDetails.doctor.firstName || ''} ${patDetails.doctor.lastName || ''}`.trim(),
        qualification: "DM, DNB MEDICAL ONCOLOGY", // Default or from API if available
      });
    }

    const allergies = patDetails.patientAllergies && patDetails.patientAllergies.length > 0 
    ? patDetails.patientAllergies
      .filter(allergy => allergy.allergiesMstDTO.allergiesId !== 9) // Exclude "Other" if needed
      .map(allergy => allergy.allergiesMstDTO.allergiesName)
      .join(", ") 
    : 'No Allergies';

    const comorbidities = patDetails.patientComorbidities && patDetails.patientComorbidities.length > 0 
    ? patDetails.patientComorbidities
      .filter(comorbidity => comorbidity.comorbiditiesDto.comorbiditiesId !== 39) // Exclude "Other" if needed
      .map(comorbidity => comorbidity.comorbiditiesDto.comorbiditiesName)
      .join(", ") 
    : 'No Comorbidities';

    const cancerSites = data.patInitiAssesmentDto?.patientCancerSiteDTOList || [];
    const currentDiagnosis = cancerSites
    .filter(site => site.activeInd === 'Y')
    .map(site => site.cancerSiteMstDto.cancerSiteName)
    .join(", ");

    setDiagnosis({
    primarySite: currentDiagnosis || '',
    allergies: allergies,
    comorbidities: comorbidities,
    });

    // Set MDT plan
    const treatmentPlan = data.patTreatmentPlanDTO || {};

    // Extract protocol name and details
    const protocolName = treatmentPlan.protocolMstDTO?.protocolName || '';

    // Calculate interval between cycles
    let cycleInterval = null;
    if (treatmentPlan.patientChemoScheduleDTOList && treatmentPlan.patientChemoScheduleDTOList.length >= 2) {
      const sortedChemos = treatmentPlan.patientChemoScheduleDTOList
        .filter(chemo => chemo.activeInd === 'Y')
        .sort((a, b) => new Date(a.chemoDt) - new Date(b.chemoDt));
      
      if (sortedChemos.length >= 2) {
        const firstDate = new Date(sortedChemos[0].chemoDt);
        const secondDate = new Date(sortedChemos[1].chemoDt);
        cycleInterval = Math.round((secondDate - firstDate) / (1000 * 60 * 60 * 24));
      }
    }

    // The cycle number will be updated by fetchCurrentCycleInfo
    
    setMdtPlan(prev => {
      return {
      plan: protocolName,
      regimen: protocolName,
      regimenPhase: treatmentPlan.chemoPhaseRegimen || "Induction",
      cycle: treatmentPlan.currentCycle || prev.cycle || 1,
      day: treatmentPlan.currentDay || 1,
      plannedCycles: treatmentPlan.cyclesPlanned || '',
      frequency: treatmentPlan.scheduleMstDTO?.scheduleName || '',
      interval: cycleInterval || treatmentPlan.scheduleMstDTO?.scheduleDays || '',
      intent: treatmentPlan.intent === 'C' ? 'Curative' : 'Palliative',
      height: patDetails.height || '',
      weight: patDetails.weight || '',
      bsa: patDetails.bsa || '',
      firstChemoDate: ''
      };
    });



    // Set the first chemo cycle date
    const firstChemoCycle = (treatmentPlan.patientChemoScheduleDTOList || [])
      .find(chemo => chemo.chemoCycleNumber === 1 && chemo.activeInd === 'Y');
    
    if (firstChemoCycle) {
      const firstDate = new Date(firstChemoCycle.chemoDt);
      const formattedDate = `${firstDate.getDate().toString().padStart(2, '0')}/${(firstDate.getMonth() + 1).toString().padStart(2, '0')}/${firstDate.getFullYear()}`;
      setMdtPlan(prev => ({...prev, firstChemoDate: formattedDate}));
    }

    // For next scheduled appointment
    const futureChemos = (treatmentPlan.patientChemoScheduleDTOList || [])
      .filter(chemo => chemo.activeInd === 'Y' && new Date(chemo.chemoDt) > new Date())
      .sort((a, b) => new Date(a.chemoDt) - new Date(b.chemoDt));
    
    if (futureChemos.length > 0) {
      const nextDate = new Date(futureChemos[0].chemoDt);
      // Condition B — fallback only. Skipped once the saved sitting date (condition A)
      // has been applied, whichever order the two fetches resolved in.
      if (nextVisitDateSourceRef.current !== 'sitting') {
        nextVisitDateSourceRef.current = 'schedule';
        setNextVisitDate(moment(nextDate).format('YYYY-MM-DD'));
      }
      setDischargeInstructions(`FOLLOW-UP FOR NEXT CHEMO CYCLE #C${futureChemos[0].chemoCycleNumber} ON ${nextDate.toLocaleDateString('en-US')} WITH CBC REPORT.`);
    }
    
    // Set chemotherapy section based on regimen
    if (protocolName.includes('Rituximab')) {
      setChemotherapy([
        {
          drugName: "INJ RITUXIMAB 375 mg/m2",
          finalDosage: true,
          notes: "IN 500 ML 0.9% NaCl IV OVER 5 HOURS",
        }
      ]);
    }
  };



  const fetchPostDischargeMedications = async (patientId) => {
    try {
      const response = await fetch(
        `${C.getUrl()}/postDischargeMedicationXref/getPatientPostDischargeMedications?patientId=${patientId}`
      );
      const result = await response.json();
      
      if (
        result.statusCode === 200 &&
        result.message &&
        result.message !== "No records found"
      ) {
        const medicines = JSON.parse(result.message);
        
        if (medicines.length > 0) {
          // Parse the duration into value and unit
          const postDischargeMeds = medicines.map((m) => {
            let durationValue = "";
            let durationUnit = "Days";
  
            if (m.duration) {
              const durParts = m.duration.split(" ");
              if (durParts.length >= 2) {
                durationValue = durParts[0];
                durationUnit = durParts[1];
              } else {
                durationValue = m.duration;
              }
            }
  
            return {
              medicine: m.medicineName,
              dose: m.dose,
              when: m.when,
              duration: m.duration,
              quantity: "", // Add default value if available in your API
              unit: durationUnit || "",
              instructions: m.instructions
            };
          });
          
          setPostDischargeMedications(postDischargeMeds);
        } else {
          // Set default values if no active medicines found
          setDefaultPostDischargeMedications();
        }
      } else {
        // Set default values if no medicines found
        setDefaultPostDischargeMedications();
      }
    } catch (err) {
      setDefaultPostDischargeMedications();
    }
  };
  
  // Helper function to set default values
  const setDefaultPostDischargeMedications = () => {
    setPostDischargeMedications([]);
  };

  // const fetchDischargeSummary = async () => {
  //   try {
  //     const response = await fetch(`${C.getUrl()}/userCase/getChemoSittingDetails?patientId=${patientId}&chemoSittingId=${chemoSittingId}`);
  //     const data = await response.json();

  //     if (data.statusCode === 200 && data.message) {
  //       let summaryData;
  //       try {
  //         summaryData = typeof data.message === 'string' ? JSON.parse(data.message) : data.message;
          
  //         if (summaryData.patientChemoSittingDTO) {
  //           const chemoData = summaryData.patientChemoSittingDTO;
  //           setDischargeInstructions(chemoData.dischargeInstructions || '');
  //           setNextVisitDate(chemoData.nextVisitDate ? new Date(chemoData.nextVisitDate).toISOString().split('T')[0] : '');
  //           setDischargeDate(chemoData.dischargeDate ? new Date(chemoData.dischargeDate).toISOString().split('T')[0] : '');
  //           setDischargeSummaryStatus(chemoData.dischargeSummaryStatus || 'PENDING');
  //         }
  //       } catch (parseError) {
  //         console.error('Error parsing discharge summary:', parseError);
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error fetching discharge summary:', error);
  //   }
  // };
  // 1. First, modify the fetchDischargeSummary function to extract both date and time

const fetchDischargeSummary = async () => {
  try {
    const response = await fetch(`${C.getUrl()}/userCase/getChemoSittingDetails?patientId=${patientId}&chemoSittingId=${chemoSittingId}`);
    const data = await response.json();

    if (data.statusCode === 200 && data.message) {
      let summaryData;
      try {
        summaryData = typeof data.message === 'string' ? JSON.parse(data.message) : data.message;
        
        if (summaryData.patientChemoSittingDTO) {
          const chemoData = summaryData.patientChemoSittingDTO;
          // Disable editing once chemo has been given (tolerate number/string/boolean forms)
          setChemoGiven(Number(chemoData.chemoGiven) === 1);
          setDischargeInstructions(chemoData.dischargeInstructions || '');
          if (chemoData.nextVisitDate) {
            // Condition A — a date is saved on the sitting. Always wins.
            // DB is UTC — append Z so JS parses as UTC, then read local date components
            const nvdStr = chemoData.nextVisitDate.includes('Z') || chemoData.nextVisitDate.includes('+') ? chemoData.nextVisitDate : chemoData.nextVisitDate + 'Z';
            const nvd = new Date(nvdStr);
            nextVisitDateSourceRef.current = 'sitting';
            setNextVisitDate(`${nvd.getFullYear()}-${String(nvd.getMonth() + 1).padStart(2, '0')}-${String(nvd.getDate()).padStart(2, '0')}`);
          } else if (nextVisitDateSourceRef.current !== 'schedule') {
            // Nothing saved and no schedule fallback applied yet — clear. Guarded so a
            // fallback that resolved first is not wiped out; without the guard the field
            // blanks out whenever next_visit_date is null, even with a cycle scheduled.
            nextVisitDateSourceRef.current = null;
            setNextVisitDate('');
          }

          // Extract both date and time from the discharge_date field
          if (chemoData.dischargeDate) {
            // DB is UTC — append Z so JS parses as UTC, then read local date/time components (IST = UTC+5:30)
            const rawStr = chemoData.dischargeDate;
            const utcStr = rawStr.includes('Z') || rawStr.includes('+') ? rawStr : rawStr + 'Z';
            const dischargeDatetime = new Date(utcStr);

            // Set the date part in local (IST) time
            const year = dischargeDatetime.getFullYear();
            const month = String(dischargeDatetime.getMonth() + 1).padStart(2, '0');
            const day = String(dischargeDatetime.getDate()).padStart(2, '0');
            setDischargeDate(`${year}-${month}-${day}`);

            // Set the time part in local (IST) time
            const hours = dischargeDatetime.getHours().toString().padStart(2, '0');
            const minutes = dischargeDatetime.getMinutes().toString().padStart(2, '0');
            const seconds = dischargeDatetime.getSeconds().toString().padStart(2, '0');
            const formattedTime = `${hours}:${minutes}:${seconds}`;
            
            setPatientDetails(prevDetails => ({
              ...prevDetails,
              dischargeTime: formattedTime
            }));
          } else {
            setDischargeDate('');
            setPatientDetails(prevDetails => ({
              ...prevDetails,
              dischargeTime: ''
            }));
          }
          
          setDischargeSummaryStatus(chemoData.dischargeSummaryStatus || 'PENDING');
        }
      } catch (parseError) {
      }
    }
  } catch (error) {
  }
};

  const fetchPatientCurrentDetails = async () => {
    if (!chemoSittingId || !patientId) {
      return;
    }
  
    try {
      const response = await fetch(`${C.getUrl()}/userCase/getChemoSittingDetails?patientId=${patientId}&chemoSittingId=${chemoSittingId}`);
      
      // Check if the response is OK
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const result = await response.json();
      
      // Validate response structure
      if (!result || result.statusCode !== 200 || !result.message) {
        return;
      }
  
      let parsedData;
      try {
        parsedData = typeof result.message === 'string' ? 
          JSON.parse(result.message) : result.message;
      } catch (parseError) {
        return;
      }
  
  
      // Check for nested DTO structure
      const chemoData = parsedData.patientChemoSittingDTO || parsedData;

      if (!chemoData) {
        return;
      }

      // Disable editing once chemo has been given (tolerate number/string/boolean forms)
      setChemoGiven(Number(chemoData.chemoGiven) === 1);

      // Enhanced logging with fallback values
      const chemoSittingDetails = {
        id: chemoData.patChemoSittingId || 'N/A',
        cycleNumber: chemoData.chemoCycleNumber || 'N/A',
        chemoDate: chemoData.chemoDt || null,
        plannedDate: chemoData.chemoPlannedDt || null,
        delay: chemoData.delay || 'N/A',
        treatmentGiven: chemoData.treatmentGiven || 'N/A',
        nextChemoDate: chemoData.nextChemoCycleDate || null,
        previousChemoDate: chemoData.previousChemoCycleDate || null
      };

      if (chemoSittingDetails.cycleNumber && chemoSittingDetails.cycleNumber !== 'N/A') {
        setMdtPlan(prev => ({
          ...prev,
          cycle: chemoSittingDetails.cycleNumber
        }));
      }

      // Validate and format chemo date
      if (chemoSittingDetails.chemoDate) {
        // First, try to use moment.js with multiple formats
        const parsedDate = moment(chemoSittingDetails.chemoDate, [
          'MMM DD, YYYY, hh:mm:ss A',
          'YYYY-MM-DD HH:mm:ss',
          'YYYY-MM-DD',
          'DD/MM/YYYY'
        ]);
      
        if (parsedDate.isValid()) {
          const formattedChemoDate = parsedDate.format('DD/MM/YYYY');

          // Pre-populate Admission Date with chemo sitting date
          setPatientDetails(prev => ({
            ...prev,
            admissionDate: parsedDate.format('YYYY-MM-DD'),
            admissionTime: parsedDate.format('HH:mm:ss')
          }));

          // Important: set the state first, then call the function with the formatted date directly
          setCurrentChemoDate(formattedChemoDate);
          await fetchFollowupData(patientId, formattedChemoDate);
        } else {

          // Try to parse using JavaScript Date
          try {
            const jsDate = new Date(chemoSittingDetails.chemoDate);
            if (!isNaN(jsDate.getTime())) {
              const day = jsDate.getDate().toString().padStart(2, '0');
              const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
              const year = jsDate.getFullYear();
              const formattedChemoDate = `${day}/${month}/${year}`;

              // Pre-populate Admission Date with chemo sitting date (fallback parse)
              setPatientDetails(prev => ({
                ...prev,
                admissionDate: `${jsDate.getFullYear()}-${month}-${day}`,
                admissionTime: `${jsDate.getHours().toString().padStart(2, '0')}:${jsDate.getMinutes().toString().padStart(2, '0')}:${jsDate.getSeconds().toString().padStart(2, '0')}`
              }));

              setCurrentChemoDate(formattedChemoDate);
              
              // Pass the formatted date directly to fetchFollowupData
              await fetchFollowupData(patientId, formattedChemoDate);
            } else {
              setCurrentChemoDate(null);
              await fetchFollowupData(patientId);
            }
          } catch (e) {
            setCurrentChemoDate(null);
            await fetchFollowupData(patientId);
          }
        }
      } else {
        setCurrentChemoDate(null);
        await fetchFollowupData(patientId);
      }
  
      // Set patient data with additional safety checks
      setPatientData(parsedData);
      await fetchPatientOpVisits(patientId);
      await fetchChemoTemplateData();
    } catch (err) {
  
      // Try to fetch followup data even if chemo details failed
      try {
        await fetchFollowupData(patientId);
      } catch (followupErr) {
      }
    }
  };

  // const handleSaveInstructions = async () => {
  //   try {
  //     const payload = {
  //       dischargeInstructions: dischargeInstructions,
  //       nextVisitDate: nextVisitDate,
  //       dischargeDate: dischargeDate
  //     };

  //     const response = await fetch(`${C.getUrl()}/userCase/updateChemoInstructions/${chemoSittingId}`, {
  //       method: 'PUT',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(payload)
  //     });

  //     if (response.ok) {
  //       try {
  //         const data = await response.json();
  //         console.log('Update response:', data);
  //       } catch (parseError) {
  //         console.log('Response parsing info:', parseError);
  //       }
        
  //       setIsEditing(false);
  //       alert('Instructions updated successfully');
  //       await fetchDischargeSummary();
  //     } else {
  //       alert('Failed to update instructions. Please try again.');
  //     }
  //   } catch (error) {
  //     console.error('Error updating instructions:', error);
  //     alert('Error updating instructions. Please try again.');
  //   }
  // };
  // 2. Also modify handleSaveInstructions to save both date and time together

const handleSaveInstructions = async () => {
  try {
    // Combine the date and time fields for saving
    const combinedDischargeDateTime = dischargeDate && patientDetails.dischargeTime 
      ? `${dischargeDate} ${patientDetails.dischargeTime}`
      : dischargeDate;
    
    const payload = {
      dischargeInstructions: dischargeInstructions,
      nextVisitDate: nextVisitDate,
      dischargeDate: combinedDischargeDateTime
    };

    const response = await fetch(`${C.getUrl()}/userCase/updateChemoInstructions/${chemoSittingId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      try {
        const data = await response.json();
      } catch (parseError) {
      }
      
      setIsEditing(false);
      alert('Instructions updated successfully');
      await fetchDischargeSummary();
    } else {
      alert('Failed to update instructions. Please try again.');
    }
  } catch (error) {
    alert('Error updating instructions. Please try again.');
  }
};

  const handleDischargeInstructionsChange = (e) => {
    setDischargeInstructions(e.target.value);
  };

  const handleNextVisitDateChange = (e) => {
    // A date the user picked outranks the schedule fallback too.
    nextVisitDateSourceRef.current = 'sitting';
    setNextVisitDate(e.target.value);
  };

  const handleDischargeDateChange = (e) => {
    setDischargeDate(e.target.value);
  };

  // Function to handle printing
  const handlePrint = () => {
    window.print()
  }

  // If no data is loaded, return loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <span>Loading...</span>
      </div>
    );
  }

  // If error occurred
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        <span>{error}</span>
      </div>
    );
  }

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset the form fields to their original values
    fetchDischargeSummary();
  };

  // Single gate for every editable field on this form. It mirrors the Save button:
  // the fields are writable exactly when "Save Details" is shown AND enabled.
  // Not editing -> only "Edit Details" is shown, so nothing may be changed.
  // chemoGiven   -> the sitting is locked, so Save is disabled and so are the fields.
  const canEdit = isEditing && !chemoGiven;

  // When the form is locked the date/time fields are rendered as plain text rather than
  // a disabled <input type="date">. A disabled input still leaves a native date picker in
  // the DOM that some browsers will happily open; with no input element there is nothing
  // to open at all.
  const displayDate = (value) => {
    if (!value) return "—";
    const parsed = moment(value, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601]);
    return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : value;
  };
  const displayTime = (value) => {
    if (!value) return "";
    const parsed = moment(value, ["HH:mm:ss", "HH:mm"]);
    return parsed.isValid() ? parsed.format("HH:mm:ss") : value;
  };

  return (
    <div className="discharge-summary">
      <div className="header-controls">
        <div className="show-header-toggle">
          <input type="checkbox" id="showHeader" checked={showHeader} onChange={() => setShowHeader(!showHeader)} />
          <label htmlFor="showHeader">Show Header</label>
        </div>
        <button className="print-button" onClick={handlePrint}>
          <i className="fas fa-print"></i> Print
        </button>
      </div>

      <div className="discharge-form">
        {showHeader && (
          <div className="header-section">
            
            <div className="logo-section">
              <img 
                src={`${process.env.PUBLIC_URL}${hospitalDetails.logo}`}
                alt="Sunrise Oncology Center" 
                className="hospital-logo" 
              />
            </div>
            {/* <div className="hospital-info">
              <div className="hospital-name">{hospitalDetails.name}</div>
              <div className="hospital-subname">{hospitalDetails.subName}</div>
              <div className="hospital-address">{hospitalDetails.address}</div>
              <div className="hospital-contact">
                {hospitalDetails.phone1} | {hospitalDetails.phone2}
              </div>
            </div> */}
            <div className="hospital-info">
  <div className="hospital-name">{hospitalDetails.name}</div>
  <div className="hospital-subname">{hospitalDetails.subName}</div>
  <div className="hospital-address">{hospitalDetails.address}</div>
  <div className="hospital-contact">
    {hospitalDetails.phone1} {hospitalDetails.phone2 && `| ${hospitalDetails.phone2}`}
  </div>
  {hospitalDetails.website && (
    <div className="hospital-website">{hospitalDetails.website}</div>
  )}
  {hospitalDetails.email && (
    <div className="hospital-email">{hospitalDetails.email}</div>
  )}
</div>
            <div className="doctor-info">
              <div className="doctor-name">{doctorDetails.name}</div>
              <div className="doctor-qualification">{doctorDetails.qualification}</div>
            </div>
          </div>
        )}

        <div className="form-title">
          <h2>Discharge Summary</h2>
        </div>

        <div className="patient-info-section">
          <div className="patient-name-container">
            <div className="patient-name-age">
              <span className="patient-name-input">{patientDetails.name}</span>
              <span>, </span>
              <span className="patient-age-input">{patientDetails.age}</span>
              <span>/</span>
              <span className="patient-gender-select">{patientDetails.gender}</span>
            </div>
            <div className="patient-uhid">
              <span>UHID: </span>
              <span className="uhid-input">{patientDetails.uhid}</span>
            </div>
          </div>

          <div className="date-container">
  <div className="admission-date-container">
    <label>Admission Date:</label>
    <div className="admission-date-time">
      {canEdit ? (
        <>
          <input
            type="date"
            className="discharge-date-input"
            value={patientDetails.admissionDate || ''}
            onChange={(e) => setPatientDetails({...patientDetails, admissionDate: e.target.value})}
          />
          <input
            type="time"
            step="1"
            className="discharge-time-input"
            value={patientDetails.admissionTime || ''}
            onChange={(e) => setPatientDetails({...patientDetails, admissionTime: e.target.value})}
          />
        </>
      ) : (
        <span className="date-time-static">
          {displayDate(patientDetails.admissionDate)} {displayTime(patientDetails.admissionTime)}
        </span>
      )}
    </div>
  </div>
  
  {/* <div className="discharge-date-container">
    <label>Discharge Date:</label>
    <div className="discharge-date-time">
      <input
        type="date"
        className="discharge-date-input"
        value={dischargeDate || ''}
        onChange={handleDischargeDateChange}
      />
      <input
        type="time"
        step="1"
        className="discharge-time-input"
        value={patientDetails.dischargeTime || ''}
        onChange={(e) => setPatientDetails({...patientDetails, dischargeTime: e.target.value})}
      />
    </div>
  </div> */}
  
{/* Replace the discharge date container in your JSX with this updated version */}
<div className="discharge-date-container">
  <label>Discharge Date:</label>
  <div className="discharge-date-time">
    {canEdit ? (
      <>
        <input
          type="date"
          className="discharge-date-input"
          value={dischargeDate || ''}
          onChange={handleDischargeDateChange}
        />
        <input
          type="time"
          step="1"
          className="discharge-time-input"
          value={patientDetails.dischargeTime || ''}
          onChange={(e) => setPatientDetails({...patientDetails, dischargeTime: e.target.value})}
        />
      </>
    ) : (
      <span className="date-time-static">
        {displayDate(dischargeDate)} {displayTime(patientDetails.dischargeTime)}
      </span>
    )}
  </div>
</div>
</div>
        </div>
       
        {/* <div className="form-section diagnosis-section">
          <h3 className="section-title">Current Diagnosis</h3> */}
        
{hasCurrentDiagnosisData() && (
  <div className="form-section diagnosis-section">
    <h3 className="section-title">Current Diagnosis</h3>
    <div className="diagnosis-content">
      {/* Only show Current Diagnosis label and value if data exists */}
      {diagnosis.primarySite && (
        <div className="diagnosis-row diagnosis-row-aligned">
          <label>Current Diagnosis:</label>
          <span className="primary-site-input">{diagnosis.primarySite}</span>
        </div>
      )}

      {/* Description - displays the Diagnosis Description value */}
      {((latestDiagnosis?.diagnosisDescription && latestDiagnosis.diagnosisDescription !== 'N/A') ||
        (latestDiagnosis?.description && latestDiagnosis.description !== 'N/A')) && (
        <div className="diagnosis-row diagnosis-row-aligned">
          <label>Description:</label>
          <span className="primary-site-input">
            {latestDiagnosis.diagnosisDescription || latestDiagnosis.description}
          </span>
        </div>
      )}

      {/* Allergies and Comorbidities row - only show if at least one has data */}
      {((diagnosis.allergies && diagnosis.allergies !== 'N/A' && diagnosis.allergies !== 'No Allergies') || 
        (diagnosis.comorbidities && diagnosis.comorbidities !== 'N/A' && diagnosis.comorbidities !== 'No Comorbidities')) && (
        <div className="diagnosis-row">
          {/* Only show Allergies if data exists */}
          {diagnosis.allergies && diagnosis.allergies !== 'N/A' && diagnosis.allergies !== 'No Allergies' && (
            <div className="diagnosis-col">
              <label>Allergies:</label>
              <span>{diagnosis.allergies}</span>
            </div>
          )}
          {/* Only show Comorbidities if data exists */}
          {diagnosis.comorbidities && diagnosis.comorbidities !== 'N/A' && diagnosis.comorbidities !== 'No Comorbidities' && (
            <div className="diagnosis-col">
              <label>Comorbidities:</label>
              <span>{diagnosis.comorbidities}</span>
            </div>
          )}
        </div>
      )}

      {/* Latest Diagnosis fields */}
      {latestDiagnosis && (
        <>
          {/* Status and Diagnosis Type row */}
          {(latestDiagnosis.status || latestDiagnosis.diagnosisType) && (
            <div className="diagnosis-row">
              {latestDiagnosis.status && latestDiagnosis.status !== 'N/A' && (
                <div className="diagnosis-col">
                  <label>Status:</label>
                  <span>{latestDiagnosis.status}</span>
                </div>
              )}
              {latestDiagnosis.diagnosisType && latestDiagnosis.diagnosisType !== 'N/A' && (
                <div className="diagnosis-col">
                  <label>Diagnosis Type:</label>
                  <span>{latestDiagnosis.diagnosisType}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Primary Site */}
          {latestDiagnosis.primarySite && latestDiagnosis.primarySite !== 'N/A' && (
            <div className="diagnosis-row">
              <div className="diagnosis-col">
                <label>Primary Site:</label>
                <span>{latestDiagnosis.primarySite}</span>
              </div>
            </div>
          )}
          
          {/* Histology and Histology Grade */}
          {(((latestDiagnosis?.histologyText && latestDiagnosis.histologyText !== 'N/A') || 
             (latestDiagnosis?.histology && latestDiagnosis.histology !== 'N/A')) ||
            ((latestDiagnosis?.histologyGradeText && latestDiagnosis.histologyGradeText !== 'N/A') || 
             (latestDiagnosis?.histologyGrade && latestDiagnosis.histologyGrade !== 'N/A'))) && (
            <div className="diagnosis-row">
              {((latestDiagnosis?.histologyText && latestDiagnosis.histologyText !== 'N/A') || 
                (latestDiagnosis?.histology && latestDiagnosis.histology !== 'N/A')) && (
                <div className="diagnosis-col">
                  <label>Histology:</label>
                  <span>{latestDiagnosis?.histologyText || latestDiagnosis?.histology}</span>
                </div>
              )}
              {((latestDiagnosis?.histologyGradeText && latestDiagnosis.histologyGradeText !== 'N/A') || 
                (latestDiagnosis?.histologyGrade && latestDiagnosis.histologyGrade !== 'N/A')) && (
                <div className="diagnosis-col">
                  <label>Histology Grade:</label>
                  <span>{latestDiagnosis?.histologyGradeText || latestDiagnosis?.histologyGrade}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Biomarkers - only show if valid data exists */}
          {latestDiagnosis.biomarkers && latestDiagnosis.biomarkers.length > 0 && 
           latestDiagnosis.biomarkers.some(biomarker => 
             (biomarker.name && biomarker.name.trim() !== '') || 
             (biomarker.status && biomarker.status.trim() !== '') || 
             (biomarker.value && biomarker.value.trim() !== '')) && (
            <div className="biomarkers-section">
              <label>Biomarkers:</label>
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestDiagnosis.biomarkers
                      .filter(biomarker => 
                        (biomarker.name && biomarker.name.trim() !== '') || 
                        (biomarker.status && biomarker.status.trim() !== '') || 
                        (biomarker.value && biomarker.value.trim() !== ''))
                      .map((biomarker, idx) => (
                        <tr key={`biomarker-${idx}`}>
                          <td>{biomarker.name || ''}</td>
                          <td>{biomarker.status || ''}</td>
                          <td>{biomarker.value || ''}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </div>
)}

        {/* <div className="form-section mdt-plan-section">
          <h3 className="section-title">MDT Plan</h3> */}
         
{hasMdtPlanData() && (
  <div className="form-section mdt-plan-section">
    <h3 className="section-title">MDT Plan</h3>
    <div className="mdt-content">
      {/* Plan of Treatment */}
      {mdtPlan.regimen && (
        <div className="mdt-plan-row">
          <label>Plan of Treatment:</label>
          <span className="mdt-plan-input">{mdtPlan.regimen}</span>
        </div>
      )}
      
      {/* First details row - only show if at least one field has data */}
      {(mdtPlan.regimen || mdtPlan.regimenPhase || mdtPlan.frequency) && (
        <div className="mdt-details-row">
          {mdtPlan.regimen && (
            <div className="mdt-col">
              <label>Regimen Planned (Protocol Title):</label>
              <span>{mdtPlan.regimen}</span>
            </div>
          )}
          {mdtPlan.regimenPhase && (
            <div className="mdt-col">
              <label>Chemotherapy Phase/Regimen Due:</label>
              <span>{mdtPlan.regimenPhase}</span>
            </div>
          )}
          {mdtPlan.frequency && (
            <div className="mdt-col">
              <label>Schedule for Doctor's Visit:</label>
              <span>{mdtPlan.frequency}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Second details row */}
      {(mdtPlan.plannedCycles || mdtPlan.firstChemoDate || mdtPlan.interval) && (
        <div className="mdt-details-row">
          {mdtPlan.plannedCycles && (
            <div className="mdt-col">
              <label>Cycles Planned:</label>
              <span>{mdtPlan.plannedCycles}</span>
            </div>
          )}
          {mdtPlan.firstChemoDate && (
            <div className="mdt-col">
              <label>1st Chemo Cycle:</label>
              <span>{mdtPlan.firstChemoDate}</span>
            </div>
          )}
          {mdtPlan.interval && (
            <div className="mdt-col">
              <label>Number of days between Cycles / Interval:</label>
              <span>{mdtPlan.interval}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Third details row */}
      {(mdtPlan.cycle || mdtPlan.day || mdtPlan.intent) && (
        <div className="mdt-details-row">
          {mdtPlan.cycle && (
          <div className="mdt-col">
            <label>Current Cycle:</label>
            {/* THIS LINE DISPLAYS THE CYCLE NUMBER */}
            <span>{mdtPlan.cycle}</span> 
          </div>
        )}
          {mdtPlan.day && (
            <div className="mdt-col">
              <label>Treatment Day:</label>
              <span>{mdtPlan.day}</span>
            </div>
          )}
          {mdtPlan.intent && (
            <div className="mdt-col">
              <label>Intent:</label>
              <span>{mdtPlan.intent}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Height, Weight, BSA row */}
      {(mdtPlan.height || mdtPlan.weight || (mdtPlan.height && mdtPlan.weight) || mdtPlan.bsa) && (
        <div className="mdt-details-row">
          {mdtPlan.height && (
            <div className="mdt-col">
              <label>Height (cm):</label>
              <span>{mdtPlan.height}</span>
            </div>
          )}
          {mdtPlan.weight && (
            <div className="mdt-col">
              <label>Weight (kgs):</label>
              <span>{mdtPlan.weight}</span>
            </div>
          )}
          {(mdtPlan.height && mdtPlan.weight) || mdtPlan.bsa ? (
            <div className="mdt-col">
              <label>BSA:</label>
              <span>
                {mdtPlan.height && mdtPlan.weight
                  ? Math.sqrt((mdtPlan.height * mdtPlan.weight) / 3600).toFixed(2)
                  : mdtPlan.bsa ? mdtPlan.bsa.toFixed(2) : ""}
              </span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  </div>
)}

        
        {/* <div className="form-section signs-section">
  <h3 className="section-title">Investigations Notes</h3> */}
  {hasInvestigationsNotesData() && (
  <div className="form-section signs-section">
    <h3 className="section-title">Investigations Notes</h3>
    <div className="signs-textarea">
      {/* Vitals from OP Visits */}
      {opVisits.length > 0 && opVisits[0].vitals && opVisits[0].vitals.length > 0 && (
        <div>
          {opVisits[0].vitals.map((vital, vitalIndex) => (
            <div key={`vital-${vitalIndex}`} className="vital-record">
              <table className="vital-table">
                <tbody>
                  <tr>
                    {vital.height && <td>{vital.height} cm</td>}
                    {vital.weight && <td>{vital.weight} kg</td>}
                    {vital.pulse && <td>{vital.pulse} bpm</td>}
                    {vital.bp && <td>{vital.bp} mmHg</td>}
                    {vital.spo2 && <td>{vital.spo2} %</td>}
                    {vital.temperature && <td>{vital.temperature} °F</td>}
                    {vital.respiratoryRate && <td>{vital.respiratoryRate} BRPM</td>}
                    {vital.bloodGlucose && <td>{vital.bloodGlucose} mg/dL {vital.glucoseTestType || ''}</td>}
                    {vital.glucoseTestType && !vital.bloodGlucose && <td>{vital.glucoseTestType}</td>}
                    {vital.bsa && <td>{vital.bsa} m²</td>}
                    {vital.bmi && <td>{vital.bmi} kg/m²</td>}
                    {vital.recordTime && <td>{vital.recordTime}</td>}
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
      
      {/* Clinical Assessment */}
      {opVisits.length > 0 && opVisits[0].clinicalAssessment && opVisits[0].clinicalAssessment.onExamination && (
        <div className="examination-notes">
          <h4>On Examination:</h4>
          <p>{opVisits[0].clinicalAssessment.onExamination}</p>
        </div>
      )}
      
      {/* Laboratory Reports */}
      {(Object.keys(cbcValues).length > 0 || Object.keys(biochemistryValues).length > 0) && (
        <div className="lab-reports">
          <h4>Laboratory Reports</h4>
          <div className="lab-reports-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
            {/* CBC Report - only show if data exists */}
            {Object.keys(cbcValues).length > 0 && Object.values(cbcValues).some(value => value && value.trim() !== '') && (
              <div className="cbc-report" style={{ flex: '1', marginRight: '20px' }}>
                <h5>CBC:</h5>
                {Object.entries(cbcValues)
                  .filter(([key, value]) => value && value.trim() !== '')
                  .map(([key, value]) => (
                    <p key={`cbc-${key}`}>
                      {key}: {value} {key.toLowerCase().includes('hemoglobin') ? 'g/dL' : 
                        key.toLowerCase().includes('platelet') || key.toLowerCase().includes('anc') ? '/mm3' : ''}
                    </p>
                  ))}
              </div>
            )}
            
            {/* Biochemistry Report - only show if data exists */}
            {Object.keys(biochemistryValues).length > 0 && Object.values(biochemistryValues).some(value => value && value.trim() !== '') && (
              <div className="biochemistry-report" style={{ flex: '1' }}>
                <h5>Biochemistry:</h5>
                {Object.entries(biochemistryValues)
                  .filter(([key, value]) => value && value.trim() !== '')
                  .map(([key, value]) => (
                    <p key={`biochem-${key}`}>
                      {key}: {value} {key.toLowerCase().includes('creatinine') ? 'mg/dL' : ''}
                    </p>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* No reports message */}
      {Object.keys(cbcValues).length === 0 && Object.keys(biochemistryValues).length === 0 && 
       !(opVisits.length > 0 && opVisits[0].vitals && opVisits[0].vitals.length > 0) && 
       !(opVisits.length > 0 && opVisits[0].clinicalAssessment && opVisits[0].clinicalAssessment.onExamination) && (
        <div className="no-reports">
          <p>No lab reports available for this discharge summary</p>
        </div>
      )}
    </div>
  </div>
)}


{/* <div className="form-section investigations-section">
  <h3 className="section-title">Signs On Admission</h3> */}

{hasSignsOnAdmissionData() && (
  <div className="form-section investigations-section">
    <h3 className="section-title">Signs On Admission</h3>
    <div className="investigations-textarea">
      {/* Vital Signs - only show if data exists */}
      {Object.values(vitalSigns).some(value => value !== '') && (
        <div className="vital-signs-summary">
          <h4>Patient Vital Signs</h4>
          <div className="vital-signs-grid">
            {vitalSigns.pulse && (
              <div className="vital-item">
                <span className="vital-label">Pulse:</span> 
                <span className="vital-value">{vitalSigns.pulse} bpm</span>
              </div>
            )}
            
            {vitalSigns.temperature && (
              <div className="vital-item">
                <span className="vital-label">Temperature:</span> 
                <span className="vital-value">{vitalSigns.temperature} °F</span>
              </div>
            )}
            
            {(vitalSigns.bpSystolic || vitalSigns.bpDiastolic) && (
              <div className="vital-item">
                <span className="vital-label">Blood Pressure:</span> 
                <span className="vital-value">{vitalSigns.bpSystolic}/{vitalSigns.bpDiastolic} mmHg</span>
              </div>
            )}
            
            {vitalSigns.spO2 && (
              <div className="vital-item">
                <span className="vital-label">SpO2:</span> 
                <span className="vital-value">{vitalSigns.spO2}%</span>
              </div>
            )}
            
            {vitalSigns.bloodGlucose && (
              <div className="vital-item">
                <span className="vital-label">Blood Glucose:</span> 
                <span className="vital-value">{vitalSigns.bloodGlucose} mg/dL</span>
              </div>
            )}
            
            {vitalSigns.glucoseTestType && (
              <div className="vital-item">
                <span className="vital-label">Glucose Test Type:</span> 
                <span className="vital-value">{vitalSigns.glucoseTestType}</span>
              </div>
            )}
            
            {vitalSigns.bmi && (
              <div className="vital-item">
                <span className="vital-label">BMI:</span>
                <span className="vital-value">{vitalSigns.bmi}</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Adverse Effects - only show if data exists */}
      {adverseEffects && adverseEffects.length > 0 && (
        <div className="adverse-effects-section">
          <h4>Adverse Effects</h4>
          <ul className="adverse-effects-list">
            {adverseEffects.map((effect, index) => (
              <li key={`adverse-effect-${index}`}>
                {effect.reportParameterMstDTO?.reportParamName || effect.adverseEffectName || 'Unknown Adverse Effect'}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Tests Prescribed - only show if data exists */}
      {opVisits.length > 0 && opVisits[0].testsRequested && (
        <div className="test-prescribed">
          <span className="test-label">Tests Prescribed:</span> 
          <span className="test-value">{opVisits[0].testsRequested}</span>
        </div>
      )}
      
      {/* Investigation Notes - only show if data exists */}
      {investigationsNotes && (
        <div className="test-results">
          {investigationsNotes}
        </div>
      )}
    </div>
  </div>
)}
        {/* Pre-Chemo Medication section */}
          {/* <div className="form-section pre-chemo-section">
            <h3 className="section-title">Pre-Chemo Medication</h3>
            
            <div className="medication-category">
              <div className="medication-heading">Pre-Medication</div>
              <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
                {medications.preMedication.length > 0 
                  ? medications.preMedication.map(med => med.name).join(" + ")
                  : ""}
              </div>
            </div>
            
            <div className="medication-category">
              <div className="medication-heading">Supplements</div>
              <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
                {medications.supplements.length > 0 
                  ? medications.supplements.map(med => med.name).join(" + ")
                  : ""}
              </div>
            </div>
          </div> */}
        

{hasPreChemoMedicationData() && (
  <div className="form-section pre-chemo-section">
    <h3 className="section-title">Pre-Chemo Medication</h3>
    
    {/* Pre-Medication - only show if data exists */}
    {medications.preMedication && medications.preMedication.length > 0 && (
      <div className="medication-category">
        <div className="medication-heading" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Pre-Medication</div>
        <div className="medication-content" style={{ 
          backgroundColor: 'transparent',
          whiteSpace: 'pre-line',
          lineHeight: '1.5'
        }}>
          {medications.preMedication.map((med, medIndex) => (
            <div key={`premed-${medIndex}`} style={{ marginBottom: '4px' }}>
              {formatMedicationText(med.name)}
            </div>
          ))}
        </div>
      </div>
    )}
    
    {/* Supplements - only show if data exists */}
    {medications.supplements && medications.supplements.length > 0 && (
      <div className="medication-category" style={{ marginTop: '16px' }}>
        <div className="medication-heading" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Supplements</div>
        <div className="medication-content" style={{
          backgroundColor: 'transparent',
          whiteSpace: 'pre-line',
          lineHeight: '1.5'
        }}>
          {medications.supplements.map((med, medIndex) => (
            <div key={`supplement-${medIndex}`} style={{ marginBottom: '4px' }}>
              {formatMedicationText(med.name)}
            </div>
          ))}
        </div>
      </div>
    )} 
  </div>
)}

          {doctorPrescriptions && doctorPrescriptions.length > 0 ? (
  <div className="form-section doctor-prescription-section">
    <h3 className="medication-heading">Medications</h3>
    <div className="table-responsive">
      <table className="table table-bordered border">
        <thead>
          <tr>
            <th>#</th>
            <th>MEDICINE</th>
            <th>DOSE</th>
            <th>WHEN</th>
            <th>DURATION</th>
            <th>QUANTITY</th>
            <th>INSTRUCTIONS</th>
          </tr>
        </thead>
        <tbody>
          {doctorPrescriptions.map((med, index) => (
            <tr key={`doctor-prescription-${index}`}>
              <td>{index + 1}</td>
              <td>{med.medicine || '-'}</td>
              <td>{med.dose || '-'}</td>
              <td>{med.when || '-'}</td>
              <td>{med.duration || '-'}</td>
              <td>{med.quantity ? `${med.quantity} ${med.unit || ''}` : '-'}</td>
              <td>{med.instructions || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
) : null}

          {/* Chemotherapy section */}
          {/* <div className="form-section chemo-section">
            <h3 className="section-title">Chemotherapy</h3> */}
            
            {/* <div className="medication-category">
              <div className="medication-heading">Chemo Dosage Prescription</div>
              <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
                {medications.chemoDosage || ""}
              </div>
            </div> */}
            {hasChemotherapyData() && (
  <div className="form-section chemo-section">
    <h3 className="section-title">Chemotherapy</h3>
    <div className="medication-category">
      <div className="medication-heading">Chemo Dosage Prescription</div>
      <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
        {medications.chemoDosage || ""}
      </div>
    </div>
  </div>
)}
          {/* </div> */}

          {/* Post-Chemo Medication section */}
          {/* <div className="form-section post-chemo-section">
            <h3 className="section-title">Supportive Care</h3>

            {/* Additional Medications section - only show if medications exist 
            {supportiveCare ? (
              <div className="form-section post-chemo-section">
                <div className="medication-category">
                  <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
                    {supportiveCare}
                  </div>
                </div>
              </div>
            ) : null}
          </div> */}
          {hasSupportiveCareData() && (
  <div className="form-section post-chemo-section">
    <h3 className="section-title">Supportive Care</h3>
    <div className="form-section post-chemo-section">
      <div className="medication-category">
        <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
          {supportiveCare}
        </div>
      </div>
    </div>
  </div>
)}

{/* Post-Chemo Medication section */}
  <div className="form-section post-chemo-section">
            <h3 className="section-title">Post-Chemo Medication</h3>
            
            {medications.antiemeticMedication && (
              <div className="medication-category">
                <div className="medication-heading">Antiemetic Medication</div>
                <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
                  {medications.antiemeticMedication}
                </div>
              </div>
            )}
            
            {medications.gcsfMedication && (
              <div className="medication-category">
                <div className="medication-heading">GCSF Medication</div>
                <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
                  {medications.gcsfMedication}
                </div>
              </div>
            )}
            
            {medications.antithromboticMedication && (
              <div className="medication-category">
                <div className="medication-heading">Antithrombotic Medication</div>
                <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
                  {medications.antithromboticMedication}
                </div>
              </div>
            )}

            {/* Additional Medications section - only show if medications exist */}
            {additionalMedications && additionalMedications.length > 0 ? (
              <div className="form-section additional-medications-section">
                <h3 className="medication-heading">Additional Medications</h3>
                <div className="table-responsive">
                  <table className="table table-bordered border">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>MEDICINE</th>
                        <th>DOSE</th>
                        <th>WHEN</th>
                        <th>DURATION</th>
                        <th>INSTRUCTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {additionalMedications.map((med, index) => (
                        <tr key={`additional-med-${index}`}>
                          <td>{index + 1}</td>
                          <td>{med.medicine || '-'}</td>
                          <td>{med.dose || '-'}</td>
                          <td>{med.when || '-'}</td>
                          <td>{med.duration || '-'}</td>
                          <td>{med.instructions || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>



          {postDischargeMedications && postDischargeMedications.length > 0 ? (
  <div className="form-section post-discharge-section">
    <h3 className="section-title">Post Discharge Medications</h3>
    <div className="table-responsive">
      <table className="table table-bordered border">
        <thead>
          <tr>
            <th>#</th>
            <th>MEDICINE</th>
            <th>DOSE</th>
            <th>WHEN</th>
            <th>DURATION</th>
            <th>INSTRUCTIONS</th>
          </tr>
        </thead>
        <tbody>
          {postDischargeMedications.map((med, index) => (
            <tr key={`post-discharge-med-${index}`}>
              <td>{index + 1}</td>
              <td>{med.medicine || '-'}</td>
              <td>{med.dose || '-'}</td>
              <td>{med.when || '-'}</td>
              <td>{med.duration || '-'}</td>
              <td>{med.instructions || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
) : null}
          {/* </div> */}

          {/* Post-Chemo Medication section */}
          {/* <div className="form-section post-chemo-section">
            <h3 className="section-title">Supportive Care</h3>

            {/* Additional Medications section - only show if medications exist 
            {supportiveCare ? (
              <div className="form-section post-chemo-section">
                <div className="medication-category">
                  <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
                    {supportiveCare}
                  </div>
                </div>
              </div>
            ) : null}
          </div> */}
          {hasSupportiveCareData() && (
  <div className="form-section post-chemo-section">
    <h3 className="section-title">Supportive Care</h3>
    <div className="form-section post-chemo-section">
      <div className="medication-category">
        <div className="medication-content" style={{ backgroundColor: 'transparent' }}>
          {supportiveCare}
        </div>
      </div>
    </div>
  </div>
)}





<div className="form-section discharge-instructions-section">


{testsRequested && testsRequested !== "No tests requested" && (
  <div className="diagnosis-row tests-requested-section">
    <h3 className="section-title mb-3">Tests Requested:</h3>
    <div className="tests-content">
      <span>{testsRequested}</span>
    </div>
  </div>
)}
</div>

       
        {/* Discharge Instructions Section */}
        <div className="form-section discharge-instructions-section">
          <h3 className="section-title">Discharge Instructions</h3>
          {canEdit ? (
            <textarea
              className="discharge-instructions-textarea"
              value={dischargeInstructions}
              onChange={handleDischargeInstructionsChange}
              rows="4"
            ></textarea>
          ) : (
            <div className="form-control-static">{dischargeInstructions}</div>
          )}
        </div>

        <div className="next-visit-discharge-container">
          <div className="next-visit-section">
            <h3 className="section-title">Next Visit Date</h3>
            {canEdit ? (
              <input
                type="date"
                className="next-visit-input"
                value={nextVisitDate || ''}
                onChange={handleNextVisitDateChange}
              />
            ) : (
              <div className="form-control-static">{displayDate(nextVisitDate)}</div>
            )}
          </div>
          
        </div>
        <div className="form-footer">
          {isEditing ? (
            <>
              <button className="btn btn-primary" onClick={handleSaveInstructions} disabled={chemoGiven}>
                Save Details
              </button>
              <button className="btn btn-secondary ml-2" onClick={handleCancelEdit} disabled={chemoGiven}>
                Cancel
              </button>
            </>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => setIsEditing(true)}
              disabled={chemoGiven}
            >
              Edit Details
            </button>
          )}
          <button className="btn btn-info ml-2" onClick={handlePrint}>
            Print
          </button>
        </div>
      </div>
    </div>
  )
}

export default CurrentDischargeSummary